import { useState, useCallback } from 'react';
import { useHotkeys, type UseHotkeyOptions } from '@tanstack/react-hotkeys';
import type { SearchResult } from '@/services/auth/types';

type UseQuickLookOptions = {
  enabled: boolean;
  selectedResult: SearchResult | null;
  onOpen?: (result: SearchResult) => void;
  target: UseHotkeyOptions['target'];
};

export function useQuickLook({ enabled, selectedResult, onOpen, target }: UseQuickLookOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCursorAtEnd, setIsCursorAtEnd] = useState(true);

  const peekable = enabled && selectedResult != null && selectedResult.kind !== 'navigation' && isCursorAtEnd;

  useHotkeys(
    [
      {
        hotkey: 'ArrowRight',
        callback: (event) => {
          if (!peekable || selectedResult == null) return;
          event.preventDefault();
          setIsOpen(true);
          onOpen?.(selectedResult);
        },
      },
      {
        hotkey: 'ArrowLeft',
        callback: (event) => {
          if (!isOpen) return;
          event.preventDefault();
          setIsOpen(false);
        },
      },
    ],
    { enabled, ignoreInputs: false, preventDefault: false, target },
  );

  const handleCursorChange = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    setIsCursorAtEnd(input.selectionStart === input.value.length);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return { isOpen, peekable, handleCursorChange, close };
}
